import { Box, TextField, Typography } from '@mui/material';
import {
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
  StaticDatePicker,
  StaticDatePickerProps,
} from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AbsenceStatus } from '~/data/absences';
import {
  AbsenceType,
  LongAbsenceEntry,
  ShortAbsenceEntry,
} from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { LegacyHeadingWithActions } from '~/legacy-components/LegacyLayout';
import { getAdapterLocale, getMomentDateLibInstance } from '~/localization';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

import { CustomPickersDay } from './useStyles';

export const AbsenceCalendar = () => {
  const { t } = useTranslation();

  const { selectedEmployeeId } = useSelectedEmployeeId();
  const { absenceEntries, isLoading } = useEmployeeAbsenceData({
    employeeId: selectedEmployeeId,
  });

  const { shortAbsencesEntries, longAbsencesEntries } = useMemo(() => {
    const calendarEntries = absenceEntries.filter(
      (a) => ![AbsenceStatus.REJECTED, AbsenceStatus.DRAFT].includes(a.status),
    );

    return {
      shortAbsencesEntries: calendarEntries.filter(
        (a) => a.type === AbsenceType.SHORT,
      ) as ShortAbsenceEntry[],
      longAbsencesEntries: calendarEntries.filter(
        (a) => a.type === AbsenceType.LONG,
      ) as LongAbsenceEntry[],
    };
  }, [absenceEntries]);

  const [calendarValue, setCalendarValue] = useState<moment.Moment | null>(
    moment(),
  );
  const [displayedMonth, setDisplayedMonth] = useState<number | null>(
    moment().month(),
  );

  const CalendarTableHeader = useMemo(() => {
    return (
      <LegacyHeadingWithActions
        headingIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Table} />}
        heading={`${t('laAbsences')}`}
      />
    );
  }, [t]);

  const CalendarContent = useMemo(() => {
    const handleOnChange = (date: moment.Moment | null) => {
      setCalendarValue(date);
    };

    const handleMonthChange = (newDisplayedMonth: moment.Moment) => {
      setDisplayedMonth(newDisplayedMonth.month());
      setCalendarValue((prevValue) =>
        prevValue ? prevValue.month(newDisplayedMonth.month()) : null,
      );
    };

    const highlightAbsences = (
      date: moment.Moment,
      selectedDate: moment.Moment[],
      pickersDayProps: PickersDayProps<moment.Moment>,
    ) => {
      if (!calendarValue) {
        return <PickersDay {...pickersDayProps} />;
      }

      const isCurrentMonth: boolean = date.month() === displayedMonth;

      const isShortAbsenceBetween = shortAbsencesEntries.some(
        ({ startDate, endDate }) => {
          return date.isBetween(startDate, endDate, 'day', '[]');
        },
      );

      const isShortAbsenceFirstDay = shortAbsencesEntries.some(
        ({ startDate }) => {
          return date.isSame(startDate, 'day');
        },
      );

      const isShortAbsenceLastDay = shortAbsencesEntries.some(({ endDate }) => {
        return date.isSame(endDate, 'day');
      });

      const absenceType = shortAbsencesEntries.find(
        ({ startDate, endDate }) => {
          return date.isBetween(startDate, endDate, 'day', '[]');
        },
      )?.reason.code;

      const absenceStatus = shortAbsencesEntries.find(
        ({ startDate, endDate }) => {
          return date.isBetween(startDate, endDate, 'day', '[]');
        },
      )?.status;

      const isLongAbsenceBetween = longAbsencesEntries.some(
        ({ startDate, endDate }) => {
          return date.isBetween(startDate, endDate, 'day', '[]');
        },
      );

      const isLongAbsenceFirstDay = longAbsencesEntries.some(
        ({ startDate }) => {
          return date.isSame(startDate, 'day');
        },
      );

      const isLongAbsenceLastDay = longAbsencesEntries.some(({ endDate }) => {
        return date.isSame(endDate, 'day');
      });

      const absenceStatusLong = longAbsencesEntries.find(
        ({ startDate, endDate }) => {
          return date.isBetween(startDate, endDate, 'day', '[]');
        },
      )?.status;

      return (
        <CustomPickersDay
          {...pickersDayProps}
          disableMargin
          dayIsBetween={isShortAbsenceBetween}
          isFirstDay={isShortAbsenceFirstDay}
          isLastDay={isShortAbsenceLastDay}
          absenceType={absenceType || ''}
          absenceStatus={absenceStatus || ''}
          dayIsBetweenLong={isLongAbsenceBetween}
          isFirstDayLong={isLongAbsenceFirstDay}
          isLastDayLong={isLongAbsenceLastDay}
          isCurrentMonth={isCurrentMonth}
          absenceStatusLong={absenceStatusLong || ''}
        />
      );
    };

    const LocalizedDatePicker = (
      props: StaticDatePickerProps<moment.Moment, moment.Moment>,
    ) => {
      return (
        <LocalizationProvider
          adapterLocale={getAdapterLocale()}
          dateAdapter={AdapterMoment}
          dateLibInstance={getMomentDateLibInstance()}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <StaticDatePicker
              inputFormat={'DD.MM.YYYY'}
              renderDay={highlightAbsences}
              onMonthChange={(newDisplayedMonth) =>
                handleMonthChange(newDisplayedMonth)
              }
              {...props}
            />
          </Box>
        </LocalizationProvider>
      );
    };

    return (
      <LocalizedDatePicker
        value={calendarValue}
        onChange={handleOnChange}
        renderInput={(params) => <TextField {...params} />}
        displayStaticWrapperAs="desktop"
      />
    );
  }, [
    calendarValue,
    shortAbsencesEntries,
    longAbsencesEntries,
    displayedMonth,
  ]);

  const AbsenceTypes = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '600px',
      }}
    >
      <Box display="flex" alignItems="center">
        <span style={{ color: '#43a047', marginRight: '5px' }}>
          <LegacyFAIcon FAIcon={LegacyFAIcons.Circle} />
        </span>
        <Typography variant="body2">{t('holiday')}</Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <span style={{ color: '#a06a34', marginRight: '5px' }}>
          <LegacyFAIcon FAIcon={LegacyFAIcons.Circle} />
        </span>
        <Typography variant="body2">{t('militaryLeave')}</Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <span style={{ color: '#d70000', marginRight: '5px' }}>
          <LegacyFAIcon FAIcon={LegacyFAIcons.Circle} />
        </span>
        <Typography variant="body2">{t('sickLeave')}</Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <span style={{ color: '#196400', marginRight: '5px' }}>
          <LegacyFAIcon FAIcon={LegacyFAIcons.Circle} />
        </span>
        <Typography variant="body2">{t('publicHoliday')}</Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <span style={{ color: 'purple', marginRight: '5px' }}>
          <LegacyFAIcon FAIcon={LegacyFAIcons.Circle} />
        </span>
        <Typography variant="body2">{t('laLongAbsence')}</Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <span style={{ color: '#1457cb', marginRight: '5px' }}>
          <LegacyFAIcon FAIcon={LegacyFAIcons.Circle} />
        </span>
        <Typography variant="body2">{t('laOther')}</Typography>
      </Box>
    </div>
  );

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <>
      {CalendarTableHeader}
      {CalendarContent}
      {AbsenceTypes}
    </>
  );
};
